import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useEditions, registrationCta } from "@/lib/useEditions";

const staticLinks = [
  { to: "/programme", label: "Programme", testid: "nav-link-programme" },
  { to: "/intervenants", label: "Intervenants", testid: "nav-link-intervenants" },
  { to: "/blog", label: "Expertises", testid: "nav-link-blog" },
  { to: "/partenaires", label: "Partenaires", testid: "nav-link-partenaires" },
  { to: "/archives", label: "Archives", testid: "nav-link-archives" },
  { to: "/a-propos", label: "À propos", testid: "nav-link-apropos" },
];

const PublicNav = () => {
  const { active, next } = useEditions();
  const cta = registrationCta(active, next);
  const editionLink = {
    to: active?.slug ? `/archives/${active.slug}` : "/edition-2026",
    label: active?.year ? `Édition ${active.year}` : "Édition",
    testid: "nav-link-edition",
  };
  const links = [editionLink, ...staticLinks];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const dark = scrolled || open;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        dark ? "bg-f2b-black/95 backdrop-blur-md border-b border-white/10 py-3" : "bg-transparent py-5"
      }`}
      data-testid="public-nav"
    >
      <div className="f2b-container flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="nav-logo" aria-label="FACADES2build — Accueil">
          <img
            src="/brand/f2b-logo-light.png"
            alt="FACADES2build"
            className={`w-auto transition-all duration-500 ${dark ? "h-9" : "h-11"}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`f2b-link-underline text-sm tracking-wide whitespace-nowrap transition-colors ${
                location.pathname === l.to ? "text-f2b-cyan" : "text-white/85 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {cta.show && (cta.external ? (
            <a
              href={cta.to}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-cta-2028"
              className="ml-2 px-5 py-2.5 bg-f2b-cyan text-f2b-black text-sm font-medium hover:shadow-[0_0_22px_rgba(0,190,255,0.5)] transition-all"
            >
              {cta.labelShort}
            </a>
          ) : (
            <Link
              to={cta.to}
              data-testid="nav-cta-2028"
              className="ml-2 px-5 py-2.5 bg-f2b-cyan text-f2b-black text-sm font-medium hover:shadow-[0_0_22px_rgba(0,190,255,0.5)] transition-all"
            >
              {cta.labelShort}
            </Link>
          ))}
        </nav>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <nav className="lg:hidden mt-3 border-t border-white/10 pt-4 pb-2 f2b-container flex flex-col gap-1" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`m-${l.testid}`}
              className="py-3 text-white/90 border-b border-white/5 text-base">{l.label}</Link>
          ))}
          {cta.show && (cta.external ? (
            <a href={cta.to} target="_blank" rel="noreferrer" className="mt-3 px-5 py-3 bg-f2b-cyan text-f2b-black text-center font-medium">
              {cta.labelShort}
            </a>
          ) : (
            <Link to={cta.to} className="mt-3 px-5 py-3 bg-f2b-cyan text-f2b-black text-center font-medium">
              {cta.labelShort}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
};

export default PublicNav;
